import React from 'react';
import { Timer, Activity, Heart, Package, Download, ChevronLeft, ChevronRight, ArrowRight, MousePointer, Smartphone, CalendarCheck, Twitter } from 'lucide-react';

export default function LandingPage() {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const [direction, setDirection] = React.useState('right');
  const [isHovered, setIsHovered] = React.useState(false);
  const [scrollY, setScrollY] = React.useState(0);

  // Parallax effect on scroll
  React.useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const screenshots = [
    {
      title: "Mi Progreso",
      description: ["Seguimiento ", "diario", " y ", "semanal", " de tu consumo con ", "insights personalizados", "."],
      mockup: "/images/screenshot1.jpeg"
    },
    {
      title: "Estadísticas",
      description: ["Visualiza tus ", "gastos", " y ", "patrones de consumo", " con ", "gráficos detallados", "."],
      mockup: "/images/screenshot2.jpeg"
    },
    {
      title: "Mi Salud",
      description: ["Monitorea tu ", "progreso de salud", " y accede a ", "ejercicios de respiración", " personalizados."],
      mockup: "/images/screenshot3.jpeg"
    },
    {
      title: "Registro Simple",
      description: ["Interfaz ", "intuitiva", " para registrar tu consumo con un ", "solo toque", "."],
      mockup: "/images/screenshot4.jpeg"
    },
    {
      title: "Insights",
      description: ["Análisis ", "detallado", " de tus ", "patrones", " y ", "hábitos de consumo", "."],
      mockup: "/images/screenshot5.jpeg"
    }
  ];

  const features = [
    { 
      icon: Timer, 
      title: "Seguimiento en Tiempo Real", 
      description: "Registra y visualiza tu consumo diario con estadísticas detalladas y métricas personalizadas." 
    },
    { 
      icon: Activity, 
      title: "Análisis de Patrones", 
      description: "Comprende tus hábitos con insights personalizados y recomendaciones basadas en datos." 
    },
    { 
      icon: Package, 
      title: "Control de Gastos", 
      description: "Calcula cuánto gastas en tu vicio." 
    },
    { 
      icon: CalendarCheck, 
      title: "Wrapped", 
      description: "Consulta tus estadísticas totales a final de año en un Wrapped con tu resumen completo." 
    },
    
  ];

  const handleTransition = (newIndex, newDirection) => {
    setIsTransitioning(true);
    setDirection(newDirection);
    setTimeout(() => {
      setCurrentIndex(newIndex);
      setIsTransitioning(false);
    }, 300);
  };

  const nextSlide = () => {
    const newIndex = currentIndex === screenshots.length - 1 ? 0 : currentIndex + 1;
    handleTransition(newIndex, 'right');
  };

  const prevSlide = () => {
    const newIndex = currentIndex === 0 ? screenshots.length - 1 : currentIndex - 1;
    handleTransition(newIndex, 'left');
  };

  React.useEffect(() => {
    if (!isHovered) {
      const interval = setInterval(nextSlide, 5000);
      return () => clearInterval(interval);
    }
  }, [isHovered, currentIndex]);

   // Feature Card Component - Ahora responsive
   const FeatureCard = ({ icon: Icon, title, description }) => {
    const [isHovered, setIsHovered] = React.useState(false);
    
    return (
      <div 
        className="relative group h-full"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="absolute -inset-[1px] bg-gradient-to-r from-purple-500/50 via-purple-600/50 to-purple-700/50 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-all duration-500" />
        
        <div className="relative flex flex-col h-full p-4 sm:p-8 rounded-2xl bg-gradient-to-br from-gray-900/90 via-gray-900 to-gray-800 backdrop-blur-xl border border-purple-500/10 hover:border-purple-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-purple-500/20 transform hover:-translate-y-1">
          <div className="relative mb-4 sm:mb-6">
            <div className={`absolute inset-0 bg-purple-500/20 rounded-full blur-xl transition-transform duration-500 ${isHovered ? 'scale-150 opacity-50' : 'scale-100 opacity-0'}`} />
            <div className="relative w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-purple-500/20 to-purple-700/20 flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
              <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-purple-400 group-hover:text-purple-300 transition-colors duration-300" />
            </div>
          </div>
  
          <div className="flex-1 flex flex-col min-h-[120px] sm:min-h-[180px]">
            <h3 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-white via-purple-100 to-white bg-clip-text text-transparent group-hover:from-purple-200 group-hover:via-purple-300 group-hover:to-purple-200 transition-all duration-300 mb-2 sm:mb-4">
              {title}
            </h3>
            <p className="text-sm sm:text-base text-slate-400 group-hover:text-slate-300 transition-colors duration-300 leading-relaxed flex-1">
              {description}
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Hero Section Component
  // Hero Section Component - Ahora responsive
  const Hero = () => (
    <div className="relative min-h-screen flex items-center justify-center px-4">
      {/* Fondo con gradientes - Sin cambios */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/30 via-purple-800/20 to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-purple-900/20 via-purple-800/10 to-transparent" />
        
        {/* Partículas decorativas - Sin cambios */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: `${Math.random() * 4 + 2}px`,
                height: `${Math.random() * 4 + 2}px`,
                backgroundColor: `rgba(168, 85, 247, ${Math.random() * 0.3})`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${Math.random() * 10 + 5}s`
              }}
            />
          ))}
        </div>
      </div>

      {/* Contenido del Hero - Ahora responsive */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-32 text-center">
        <div className="space-y-6 sm:space-y-8">
          <h1 className="text-5xl sm:text-7xl font-bold bg-gradient-to-r from-purple-300 via-purple-500 to-purple-300 inline-block text-transparent bg-clip-text animate-gradient">
            YoFumo
          </h1>
          
          <p className="text-xl sm:text-2xl text-slate-300 max-w-2xl mx-auto px-4">
            Toma el control de tus hábitos y consulta tus estadísticas
          </p>
          
          <div className="flex flex-col items-center gap-4">
            <a
              href="https://apps.apple.com/es/app/yofumo/id6739218405"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 rounded-full bg-gradient-to-r from-purple-500 to-purple-700 text-white font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-xl hover:shadow-purple-500/25"
            >
              <Download className="w-5 h-5 mr-2 transition-transform group-hover:scale-110" />
              <span className="text-sm sm:text-base">Descargar en App Store</span>
              <span className="absolute -inset-0.5 rounded-full blur opacity-0 group-hover:opacity-50 transition duration-300 bg-purple-400" />
            </a>
            
            <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm text-slate-400">
              <span className="flex items-center">
                <MousePointer className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> Instalación Simple
              </span>
              <span className="flex items-center">
                <Smartphone className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> iOS 14+
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  

  return (
    <div className="min-h-screen bg-black text-slate-100">
      <Hero />

      {/* Features Section - Ahora responsive */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/10 via-transparent to-transparent" />
        <div className="absolute -top-40 left-0 w-96 h-96 bg-purple-700/20 rounded-full blur-3xl opacity-30" />
        <div className="absolute -bottom-40 right-0 w-96 h-96 bg-purple-800/20 rounded-full blur-3xl opacity-30" />

        <div className="relative text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-purple-300 via-purple-400 to-purple-300 inline-block text-transparent bg-clip-text mb-4">
            Características Principales
          </h2>
          <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto px-4">
            Herramientas diseñadas para ayudarte a conocerte mejor
          </p>
        </div>

        <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
          {features.map((feature, index) => (
            <FeatureCard key={index} {...feature} />
          ))}
        </div>
      </div>

      {/* Screenshots Section - Ahora responsive */}
      <div className="w-full bg-black py-16 sm:py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-purple-400 to-purple-600 inline-block text-transparent bg-clip-text mb-4">
              Una app diseñada para ti
            </h2>
            <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto px-4">
              Interfaz intuitiva y moderna que te ayuda a visualizar y comprender tus hábitos
            </p>
          </div>

          <div className="relative mb-16 sm:mb-24">
            <div 
              className="flex items-center justify-center gap-4 sm:gap-12"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <button 
                onClick={prevSlide}
                className="p-2 sm:p-3 rounded-full bg-purple-500/20 hover:bg-purple-500/30 transition-all duration-300 backdrop-blur-lg transform hover:scale-110 hover:shadow-lg hover:shadow-purple-500/25"
                disabled={isTransitioning}
              >
                <ChevronLeft className="w-6 h-6 sm:w-8 sm:h-8 text-purple-400" />
              </button>

              {/* Container del mockup con aspect ratio mejorado */}
              <div className="relative w-[240px] h-[480px] sm:w-[280px] sm:h-[560px] md:w-[320px] md:h-[640px] lg:w-[360px] lg:h-[720px]">
                <div className="absolute inset-0 bg-gradient-to-b from-purple-600/30 via-purple-900/30 to-purple-600/30 rounded-[2rem] blur-xl transform transition-all duration-500 scale-105" />
                
                <div className="relative h-full w-full p-2 sm:p-3 rounded-[2rem] backdrop-blur-xl bg-black/30 border border-purple-500/20 shadow-2xl transition-all duration-500 group hover:border-purple-500/40 hover:shadow-purple-500/20">
                  <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-white/10 to-transparent pointer-events-none" />
                  
                  <div className="relative w-full h-full overflow-hidden rounded-[1.8rem] bg-black">
                    <div 
                      className={`w-full h-full transition-transform duration-300 ease-in-out ${
                        isTransitioning 
                          ? direction === 'right' 
                            ? '-translate-x-full' 
                            : 'translate-x-full'
                          : 'translate-x-0'
                      }`}
                    >
                      <img 
                        src={screenshots[currentIndex].mockup}
                        alt={screenshots[currentIndex].title}
                        className="w-full h-full object-cover object-center"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <button 
                onClick={nextSlide}
                className="p-2 sm:p-3 rounded-full bg-purple-500/20 hover:bg-purple-500/30 transition-all duration-300 backdrop-blur-lg transform hover:scale-110 hover:shadow-lg hover:shadow-purple-500/25"
                disabled={isTransitioning}
              >
                <ChevronRight className="w-6 h-6 sm:w-8 sm:h-8 text-purple-400" />
              </button>
            </div>
          </div>

          {/* Enhanced description card */}
          <div className="relative mt-16 sm:mt-32 max-w-3xl mx-auto px-4">
            <div className="absolute -top-20 -left-20 w-48 h-48 sm:w-64 sm:h-64 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
            <div className="absolute -bottom-20 -right-20 w-48 h-48 sm:w-64 sm:h-64 bg-purple-600/20 rounded-full blur-3xl animate-pulse" />
            
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-1000 group-hover:duration-200 animate-gradient-xy"></div>
              
              <div className="relative bg-gray-900/40 backdrop-blur-xl rounded-2xl p-6 sm:p-12 shadow-2xl">
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-transparent via-purple-500 to-transparent" />
                
                <div className="text-center space-y-4 sm:space-y-6">
                  <h3 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-purple-400 via-purple-300 to-purple-400 bg-clip-text text-transparent animate-gradient">
                    {screenshots[currentIndex].title}
                  </h3>

                  <p className="text-xl sm:text-2xl leading-relaxed">
                    {screenshots[currentIndex].description.map((text, index) => (
                      <span 
                        key={index} 
                        className={`transition-colors duration-300 ${
                          index % 2 === 1 
                            ? "text-purple-400 font-medium" 
                            : "text-slate-300"
                        }`}
                      >
                        {text}
                      </span>
                    ))}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  
        {/* App Preview Section - Ahora responsive */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-purple-600 inline-block text-transparent bg-clip-text">
              Una interfaz diseñada para ti
            </h2>
            <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto px-4">
              Diseño intuitivo y fácil de usar que te ayuda a mantener el control y visualizar tu progreso
            </p>
          </div>
  
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-8">
            {[
              {
                title: "Dashboard Personal",
                description: "Visualiza tu progreso diario y mantén el control de tus objetivos con nuestro dashboard personalizado.",
                items: ["Estadísticas diarias", "Seguimiento de progreso", "Insights personalizados"],
                gradient: "from-purple-500 via-fuchsia-500 to-purple-600"
              },
              {
                title: "Análisis Detallado",
                description: "Comprende tus patrones y hábitos con análisis profundos y visualizaciones interactivas.",
                items: ["Patrones de consumo", "Gráficas interactivas", "Tendencias semanales"],
                gradient: "from-fuchsia-500 via-purple-500 to-fuchsia-600"
              },
              {
                title: "Salud y Bienestar",
                description: "Mejora tu bienestar con ejercicios personalizados y seguimiento de tu progreso en salud.",
                items: ["Beneficios por etapa", "Ejercicios de respiración", "Tips de bienestar"],
                gradient: "from-purple-600 via-fuchsia-600 to-purple-500"
              }
            ].map((section, index) => (
              <div 
                key={index}
                className="group relative"
              >
                <div className="absolute -inset-[1px] bg-gradient-to-r from-purple-500/50 via-purple-600/50 to-purple-700/50 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-all duration-500" />
                
                <div className="relative p-4 sm:p-8 rounded-2xl bg-gradient-to-br from-gray-900/90 via-gray-900 to-gray-800 border border-purple-500/10 hover:border-purple-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-purple-500/20 overflow-hidden">
                  <div className="absolute -right-20 -top-20 w-40 h-40 bg-gradient-to-br from-purple-500/30 to-fuchsia-500/30 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-all duration-700 group-hover:scale-150" />
                  
                  <div className="relative z-10">
                    <h3 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-white via-purple-100 to-white bg-clip-text text-transparent group-hover:from-purple-200 group-hover:via-purple-300 group-hover:to-purple-200 transition-all duration-300 mb-4">
                      {section.title}
                    </h3>
                    
                    <p className="text-sm sm:text-base text-slate-400 group-hover:text-slate-300 transition-colors duration-300 mb-6">
                      {section.description}
                    </p>
                    
                    <ul className="space-y-3 sm:space-y-4">
                      {section.items.map((item, itemIndex) => (
                        <li 
                          key={itemIndex} 
                          className="flex items-center group/item"
                        >
                          <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 rounded-lg bg-purple-500/10 group-hover:bg-purple-500/20 flex items-center justify-center mr-3 transition-all duration-300">
                            <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 text-purple-400 group-hover/item:text-purple-300 transform group-hover/item:translate-x-1 transition-all duration-300" />
                          </div>
                          <span className="text-sm sm:text-base text-slate-300 group-hover/item:text-white transition-colors duration-300">
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r from-transparent via-purple-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500" />
                </div>
              </div>
            ))}
          </div>
        </div>
  
        {/* Updated Footer with Social Media */}
        <footer className="relative border-t border-gray-800">
          <div className="absolute inset-0 bg-gradient-to-t from-purple-900/10 to-transparent" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <div className="flex flex-col items-center gap-8 sm:gap-12">
              <div className="text-center space-y-6">
                <a
                  href="https://apps.apple.com/es/app/yofumo/id6739218405"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 rounded-full bg-gradient-to-r from-purple-500 to-purple-700 text-white font-semibold hover:from-purple-600 hover:to-purple-800 transition-all transform hover:scale-105 hover:shadow-xl hover:shadow-purple-500/25"
                >
                  <Download className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  <span className="text-sm sm:text-base">Descargar en App Store</span>
                </a>
              </div>

              {/* Social Media Links */}
              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8">
                <a
                  href="https://twitter.com/yofumoapp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2 text-slate-400 hover:text-purple-400 transition-colors duration-300"
                >
                  <div className="p-2 rounded-full bg-purple-500/10 group-hover:bg-purple-500/20 transition-colors duration-300">
                    <Twitter className="w-4 h-4 sm:w-5 sm:h-5" />
                  </div>
                  <span className="text-sm sm:text-base">@yofumoapp</span>
                </a>
                <a
                  href="https://twitter.com/juansinmangas"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2 text-slate-400 hover:text-purple-400 transition-colors duration-300"
                >
                  <div className="p-2 rounded-full bg-purple-500/10 group-hover:bg-purple-500/20 transition-colors duration-300">
                    <Twitter className="w-4 h-4 sm:w-5 sm:h-5" />
                  </div>
                  <span className="text-sm sm:text-base">@juansinmangas</span>
                </a>
              </div>

              <div className="text-sm sm:text-base text-slate-400 hover:text-slate-300 transition-colors text-center px-4">
                <p>© 2025 YoFumo. Todos los derechos reservados.</p>
              </div>
            </div>
          </div>
        </footer>
  
        {/* Global styles para animaciones - Sin cambios */}
        <style jsx global>{`
          @keyframes float {
            0%, 100% { 
              transform: translateY(0px) translateZ(0); 
            }
            50% { 
              transform: translateY(-20px) translateZ(0); 
            }
          }
  
          @keyframes gradient {
            0% { 
              background-position: 0% 50%;
              transform: translateZ(0);
            }
            50% { 
              background-position: 100% 50%;
              transform: translateZ(0);
            }
            100% { 
              background-position: 0% 50%;
              transform: translateZ(0);
            }
          }
  
          .animate-float {
            animation: float 6s ease-in-out infinite;
            will-change: transform;
          }
  
          .animate-gradient {
            background-size: 200% auto;
            animation: gradient 8s linear infinite;
            will-change: background-position;
          }
  
          .animate-fadeIn {
            opacity: 0;
            animation: fadeIn 1s ease-out forwards;
            will-change: transform, opacity;
          }
  
          @keyframes fadeIn {
            from { 
              opacity: 0; 
              transform: translateY(20px) translateZ(0);
            }
            to { 
              opacity: 1; 
              transform: translateY(0) translateZ(0);
            }
          }
        `}</style>
      </div>
    );
  }
